import { useCallback, useState } from 'react';
import { NodeType, OUNode } from '../types';

let nodeIdCounter = 100;
const genId = () => `ou-${++nodeIdCounter}`;

export function useOrgNodes(initialNodes: OUNode[]) {
  const [nodes, setNodes] = useState<OUNode[]>(initialNodes);

  const addNode = useCallback((name: string, type: NodeType, parentId: string | null) => {
    const newNode: OUNode = {
      id: genId(),
      name,
      type,
      parentId,
      mobileConfigId: null,
      createdAt: new Date().toISOString().split('T')[0],
      updatedAt: new Date().toISOString().split('T')[0],
    };
    setNodes(prev => [...prev, newNode]);
    return newNode.id;
  }, []);

  const renameNode = useCallback((id: string, name: string) => {
    setNodes(prev => prev.map(n =>
      n.id === id ? { ...n, name, updatedAt: new Date().toISOString().split('T')[0] } : n
    ));
  }, []);

  const convertType = useCallback((id: string, currentNodes: OUNode[]) => {
    const node = currentNodes.find(n => n.id === id);
    if (!node) return false;
    const newType: NodeType = node.type === 'Location' ? 'OrganizationalUnit' : 'Location';
    setNodes(prev => prev.map(n =>
      n.id === id ? { ...n, type: newType, updatedAt: new Date().toISOString().split('T')[0] } : n
    ));
    return true;
  }, []);

  const getDescendantIds = useCallback((id: string, currentNodes: OUNode[]): string[] => {
    const result: string[] = [];
    const collect = (parentId: string) => {
      currentNodes.filter(n => n.parentId === parentId).forEach(n => {
        result.push(n.id);
        collect(n.id);
      });
    };
    collect(id);
    return result;
  }, []);

  const convertTypeForce = useCallback((id: string) => {
    setNodes(prev => {
      const descendants = new Set<string>();
      const collect = (parentId: string) => {
        prev.filter(n => n.parentId === parentId).forEach(n => {
          descendants.add(n.id);
          collect(n.id);
        });
      };
      collect(id);
      return prev
        .filter(n => !descendants.has(n.id))
        .map(n => n.id === id
          ? { ...n, type: 'Location' as NodeType, updatedAt: new Date().toISOString().split('T')[0] }
          : n
        );
    });
  }, []);

  const deleteNode = useCallback((id: string) => {
    setNodes(prev => prev.filter(n => n.id !== id));
  }, []);

  const deleteNodeCascade = useCallback((id: string) => {
    setNodes(prev => {
      const toDelete = new Set<string>([id]);
      const collect = (parentId: string) => {
        prev.filter(n => n.parentId === parentId).forEach(n => {
          toDelete.add(n.id);
          collect(n.id);
        });
      };
      collect(id);
      return prev.filter(n => !toDelete.has(n.id));
    });
  }, []);

  const reparentNode = useCallback((id: string, newParentId: string | null) => {
    setNodes(prev => prev.map(n =>
      n.id === id ? { ...n, parentId: newParentId, updatedAt: new Date().toISOString().split('T')[0] } : n
    ));
  }, []);

  const reorderNode = useCallback((draggedId: string, targetId: string, position: 'before' | 'after', targetParentId: string | null) => {
    setNodes(prev => {
      const dragged = prev.find(n => n.id === draggedId);
      if (!dragged) return prev;
      const withoutDragged = prev.filter(n => n.id !== draggedId);
      const targetIdx = withoutDragged.findIndex(n => n.id === targetId);
      if (targetIdx === -1) return prev;
      const insertIdx = position === 'before' ? targetIdx : targetIdx + 1;
      const updated = [...withoutDragged];
      updated.splice(insertIdx, 0, {
        ...dragged,
        parentId: targetParentId,
        updatedAt: new Date().toISOString().split('T')[0],
      });
      return updated;
    });
  }, []);

  const setMobileConfig = useCallback((nodeId: string, configId: string | null) => {
    setNodes(prev => prev.map(n => {
      if (n.id === nodeId) return { ...n, mobileConfigId: configId };
      if (configId && n.mobileConfigId === configId) return { ...n, mobileConfigId: null };
      return n;
    }));
  }, []);

  return { nodes, setNodes, addNode, renameNode, convertType, convertTypeForce, getDescendantIds, deleteNode, deleteNodeCascade, reparentNode, reorderNode, setMobileConfig };
}
